import "./index.css";
import "@packages/intent-ui";

import type { EnvironmentVariables } from "@squide/firefly";
import { FireflyProvider, initializeFirefly } from "@squide/firefly";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.tsx";
import { registerGoals } from "@domains/goals/client/registerGoals.ts";
import { registerManagerImpact } from "@domains/manager-impact/client/registerManagerImpact.ts";
import { registerOrgChart } from "@domains/org-chart/client/registerOrgChart.ts";
import { registerReviews } from "@domains/reviews/client/registerReviews.ts";
import { registerRoutines } from "@domains/routines/client/registerRoutines.ts";
import { registerSegments } from "@domains/segments/client/registerSegments.ts";
import { registerAccountSettings } from "@modules/account-settings/client";
import { registerAdminSettings } from "@modules/admin-settings/client";
import { registerAnalytics } from "@modules/analytics/client";
import { registerHome } from "@modules/home/client";
import { registerOrganization } from "@modules/organization/client";
import { registerPeople } from "@modules/people/client";
import { createUnmanagedMutationErrorHandler, createUnmanagedQueryErrorHandler, registerShell } from "@modules/shell/client";
import { registerTeam } from "@modules/team/client";
import { createCommandMenuLinksRegistryPlugin } from "@packages/core-client/registration/command-menu/CommandMenuLinksPlugin.ts";
import { createI18NextPlugin } from "@packages/core-client/i18n/createI18nextPlugin.ts";
import { BrowserConsoleLogger, type RootLogger, LogLevel } from "@workleap/logging";
import type { InitializeTelemetryOptions } from "@workleap/telemetry";
import { initializeTelemetry, LogRocketLogger } from "@workleap/telemetry";
import { TelemetryProvider } from "@workleap/telemetry/react";
import { env } from "./env.ts";
import { initializeLaunchDarkly } from "./initializeLaunchDarly.ts";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createQueryClientConfig } from "./createQueryClientConfig.ts";
import { WorkleapAiClientProvider } from "@workleap-ai/client/react";
import { initializeWorkleapAiClient } from "./initializeWorkleapAiClient.ts";

function escapeRegexMetacharacters(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const appEnv = env.PUBLIC_APP_ENV;
const isMsw = appEnv === "msw";
const isLocal = isMsw || appEnv === "local";

const apiBaseUrl = isMsw ? window.location.origin : env.PUBLIC_API_URL;

const environmentVariables = {
    apiBaseUrl,
    authServiceUrl: env.PUBLIC_AUTH_SERVICE_URL,
    loginUrl: env.PUBLIC_AUTH_LOGIN_URL,
    officevibeRecognitionUrl: env.PUBLIC_OFFICEVIBE_RECOGNITION_URL,
    pingboardUrl: env.PUBLIC_PINGBOARD_URL,
    workleapClassicUrl: env.PUBLIC_WORKLEAP_CLASSIC_URL,
    workleapPerformanceUrl: env.PUBLIC_WORKLEAP_PERFORMANCE_URL,
    workleapAiUrl: env.PUBLIC_WORKLEAP_AI_URL,
    officevibeUrl: env.PUBLIC_OFFICEVIBE_URL
} satisfies EnvironmentVariables;

const loggers: RootLogger[] = [];

if (isLocal) {
    loggers.push(new BrowserConsoleLogger());
}

const launchDarklyClient = await initializeLaunchDarkly(appEnv, environmentVariables.apiBaseUrl, env.PUBLIC_LAUNCH_DARKLY_USE_REMOTE_FLAGS_FOR_LOCAL_AND_MSW, env.PUBLIC_LAUNCH_DARKLY_CLIENT_ID);

const isLogRocketEnabled = !!env.PUBLIC_LOGROCKET_APP_ID;

if (isLogRocketEnabled) {
    loggers.push(new LogRocketLogger({ logLevel: LogLevel.information }));
} else {
    loggers.forEach(x => {
        x.warning(`[web-app] Cannot register LogRocket logger because the PUBLIC_LOGROCKET_APP_ID environment variable has not been configured.`);
    });
}

const telemetryOptions: InitializeTelemetryOptions = {
    loggers
};

telemetryOptions.honeycomb = {
    namespace: "workleap-app",
    serviceName: "web",
    apiServiceUrls: [new RegExp(`^${escapeRegexMetacharacters(environmentVariables.apiBaseUrl)}`)],
    options: {
        proxy: `${environmentVariables.apiBaseUrl}/api/honeycomb`
    }
};

if (isLogRocketEnabled) {
    const rootHostname = appEnv === "prod" ? ".workleap.com" : ".workleap-stg.com";
    telemetryOptions.logRocket = {
        appId: env.PUBLIC_LOGROCKET_APP_ID!,
        options: window.location.hostname.endsWith(rootHostname) ? { rootHostname } : undefined
    };
} else {
    loggers.forEach(x => {
        x.warning(`[web-app] Cannot register LogRocket instrumentation because the PUBLIC_LOGROCKET_APP_ID environment variable has not been configured.`);
    });
}

telemetryOptions.mixpanel = {
    envOrTrackingApiBaseUrl: `${environmentVariables.apiBaseUrl}/api/mixpanel`,
    options: {
        trackingEndpoint: "track"
    }
};

const telemetryClient = initializeTelemetry("wlp", telemetryOptions);

const localModules = [registerShell, registerSegments, registerOrgChart, registerGoals, registerManagerImpact, registerReviews, registerRoutines, registerHome, registerTeam, registerPeople, registerAnalytics, registerAdminSettings, registerAccountSettings, registerOrganization];

if (isLocal) {
    const { registerDevtools } = await import("@packages/devtools/client/registerDevtools.ts");

    localModules.push(registerDevtools);
}

const fireflyRuntime = initializeFirefly({
    mode: isLocal ? "development" : "production",
    useMsw: isMsw,
    localModules,
    plugins: [x => createI18NextPlugin(x), x => createCommandMenuLinksRegistryPlugin(x)],
    startMsw: async runtime => {
        return (await import("./startMsw.ts")).startMsw(runtime.requestHandlers);
    },
    environmentVariables,
    honeycombInstrumentationClient: telemetryClient.honeycomb,
    launchDarklyClient,
    loggers
});

const queryClient = new QueryClient(
    createQueryClientConfig({
        onQueryError: createUnmanagedQueryErrorHandler(fireflyRuntime),
        onMutationError: createUnmanagedMutationErrorHandler(fireflyRuntime)
    })
);

const workleapAiClient = initializeWorkleapAiClient(appEnv, isLocal, loggers);

const root = createRoot(document.getElementById("root")!);

root.render(
    <StrictMode>
        <FireflyProvider runtime={fireflyRuntime}>
            <TelemetryProvider client={telemetryClient}>
                <QueryClientProvider client={queryClient}>
                    <WorkleapAiClientProvider client={workleapAiClient}>
                        <App />
                    </WorkleapAiClientProvider>
                </QueryClientProvider>
            </TelemetryProvider>
        </FireflyProvider>
    </StrictMode>
);
